import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { getSuscriptoresV2 } from '../../lib/suscriptoresApi'

// Sub-sprint A · Parte 1 — Suscriptores v2 (Admin UX B2C)
// Dark-on-brand: layout del mockup, paleta oficial (teal #00A8B5, ámbar en vez de gold, sin bone).
// Agrupa por titular, KPIs de negocio, filtros útiles, pill "Ocultar tests" (default on).

const fmtMoney = (n) => '$' + Math.ceil(Number(n || 0)).toLocaleString('es-UY')

function haceCuanto(ts) {
  if (!ts) return null
  const diff = Date.now() - new Date(ts).getTime()
  const min = Math.floor(diff / 60000)
  if (min < 1) return 'recién'
  if (min < 60) return `hace ${min} min`
  const h = Math.floor(min / 60)
  if (h < 24) return `hace ${h} h`
  const d = Math.floor(h / 24)
  return `hace ${d} d`
}

function iniciales(nombre) {
  if (!nombre) return '?'
  const parts = nombre.trim().split(/\s+/)
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?'
}

// estado_dominante → clase de color de avatar / badges
const AVATAR_CLASS = { activa: 'av-green', intentando: 'av-amber', cancelada: 'av-red', cuenta: 'av-gray' }

// Badges por estado (conteos) que se muestran en la columna Suscripciones
function badgesDe(t) {
  const b = []
  if (t.n_activa > 0) b.push({ cls: 'bg-green', txt: `${t.n_activa} activa${t.n_activa > 1 ? 's' : ''}` })
  if (t.n_pausada > 0) b.push({ cls: 'bg-amber', txt: `${t.n_pausada} pausada${t.n_pausada > 1 ? 's' : ''}` })
  if (t.n_pendiente > 0) b.push({ cls: 'bg-amber', txt: `${t.n_pendiente} sin activar` })
  if (t.n_cancelada > 0) b.push({ cls: 'bg-red', txt: `${t.n_cancelada} cancelada${t.n_cancelada > 1 ? 's' : ''}` })
  if (b.length === 0) b.push({ cls: 'bg-muted', txt: 'Cuenta creada' })
  return b
}

const FILTROS = [
  { key: 'activos', label: 'Activos' },
  { key: 'pausados', label: 'Pausados' },
  { key: 'cancelados', label: 'Cancelados' },
  { key: 'sin_activar', label: 'Sin activar' },
]
// Paleta pastel por estado (canónica, misma que los chips de Empresas B2B — ver _design-tokens.md)
const FILTRO_TONE = {
  activos:     { color: '#16A34A', bg: '#DCFCE7' },  // green
  pausados:    { color: '#CA8A04', bg: '#FEF9C3' },  // amber
  cancelados:  { color: '#DC2626', bg: '#FEE2E2' },  // red
  sin_activar: { color: '#64748B', bg: '#F1F5F9' },  // grey
}

export default function SuscriptoresV2() {
  const nav = useNavigate()
  const [data, setData] = useState({ kpis: {}, titulares: [] })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filtro, setFiltro] = useState('activos')
  const [busqueda, setBusqueda] = useState('')
  const [ocultarTests, setOcultarTests] = useState(true)

  useEffect(() => { load() }, [])
  async function load() {
    setLoading(true); setError(null)
    try { setData(await getSuscriptoresV2()) }
    catch (e) { setError(e.message || 'Error cargando suscriptores') }
    finally { setLoading(false) }
  }

  const kpis = data.kpis || {}
  const churnPct = (() => {
    const activas = Number(kpis.activas_total || 0)
    if (activas === 0) return null // sin base activa (no-test) → churn indefinido, no mostrar % engañoso
    const canc = Number(kpis.cancelados_30d || 0)
    return Math.round((canc / (canc + activas)) * 100)
  })()
  const mrrProm = kpis.mrr_clientes > 0 ? Math.ceil(Number(kpis.mrr_activo) / kpis.mrr_clientes) : 0

  const filtrados = useMemo(() => {
    let r = data.titulares || []
    if (ocultarTests) r = r.filter(t => !t.es_test)
    if (filtro === 'activos') r = r.filter(t => t.estado_dominante === 'activa')
    else if (filtro === 'pausados') r = r.filter(t => t.n_pausada > 0)
    else if (filtro === 'cancelados') r = r.filter(t => t.estado_dominante === 'cancelada')
    else if (filtro === 'sin_activar') r = r.filter(t => t.estado_dominante === 'intentando' || t.estado_dominante === 'cuenta')
    const q = busqueda.trim().toLowerCase()
    if (q) {
      r = r.filter(t =>
        t.nombre?.toLowerCase().includes(q) ||
        t.email?.toLowerCase().includes(q) ||
        t.phone?.toLowerCase().includes(q) ||
        t.documento?.toLowerCase().includes(q) ||
        t.barrio?.toLowerCase().includes(q)
      )
    }
    return r
  }, [data.titulares, filtro, busqueda, ocultarTests])

  const testsOcultos = ocultarTests ? (data.titulares || []).filter(t => t.es_test).length : 0
  const totalSusc = filtrados.reduce((a, t) => a + Number(t.n_susc || 0), 0)

  function exportarCSV() {
    const cols = ['nombre', 'email', 'phone', 'documento', 'barrio', 'estado_dominante', 'n_activa', 'n_pausada', 'n_pendiente', 'n_cancelada', 'total_facturado', 'cobros', 'es_test']
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`
    const lines = [cols.join(',')]
    for (const t of filtrados) lines.push(cols.map(c => esc(t[c])).join(','))
    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = `suscriptores_${new Date().toISOString().slice(0, 10)}.csv`
    a.click(); URL.revokeObjectURL(url)
  }

  return (
    <AdminLayout>
      <style>{SUSCV2_CSS}</style>
      <div className="suscv2">
        <div className="panel">

          <div className="s-header">
            <div>
              <div className="s-eyebrow">Clientes / Suscriptores</div>
              <div className="s-title">Suscriptores</div>
            </div>
            <div className="s-header-actions">
              <Link className="s-btn s-btn-primary" to="/admin/clientes?alta=1">＋ Nueva suscripción</Link>
              <button className="s-btn s-btn-secondary" onClick={exportarCSV} disabled={!filtrados.length}>⭳ Exportar</button>
            </div>
          </div>

          {/* KPIs */}
          <div className="kpi-grid">
            <div className="kpi kpi-green">
              <div className="kpi-label">MRR activo</div>
              <div className="kpi-value">{fmtMoney(kpis.mrr_activo)}</div>
              <div className="kpi-sub">{kpis.mrr_clientes || 0} cliente{kpis.mrr_clientes === 1 ? '' : 's'} · {fmtMoney(mrrProm)} promedio</div>
            </div>
            <div className="kpi kpi-sky">
              <div className="kpi-label">Nuevos · 7d</div>
              <div className="kpi-value">{kpis.nuevos_7d || 0}</div>
              <div className="kpi-sub">Suscripciones activadas</div>
            </div>
            <div className="kpi kpi-red">
              <div className="kpi-label">Churn · 30d</div>
              <div className="kpi-value">{churnPct === null ? '—' : churnPct + '%'}</div>
              <div className="kpi-sub">{churnPct === null ? 'Sin base activa todavía' : `${kpis.cancelados_30d || 0} baja(s) / ${kpis.activas_total || 0} activas`}</div>
            </div>
            <div className="kpi kpi-amber">
              <div className="kpi-label">Con intento fallido</div>
              <div className="kpi-value">{kpis.con_intento_fallido || 0}</div>
              <div className="kpi-sub">Sin activar tras intentar</div>
            </div>
          </div>

          {/* Toolbar */}
          <div className="toolbar">
            <div className="search">
              <span className="s-ic">🔍</span>
              <input value={busqueda} onChange={e => setBusqueda(e.target.value)}
                placeholder="Buscar por nombre, email, teléfono, CI, dirección…" />
            </div>
            <div className="filters">
              {FILTROS.map(f => {
                const tone = FILTRO_TONE[f.key]
                const active = filtro === f.key
                return (
                  <span key={f.key} className="pill" onClick={() => setFiltro(f.key)}
                    style={active
                      ? { background: tone.color, color: '#fff', borderColor: tone.color, fontWeight: 700 }
                      : { background: tone.bg, color: '#1A3461', borderColor: 'transparent' }}>
                    {active ? '✓ ' : ''}{f.label}
                  </span>
                )
              })}
              <span className="divider-v" />
              <span className={`pill pill-danger ${ocultarTests ? 'pill-danger-on' : ''}`} onClick={() => setOcultarTests(v => !v)}>
                {ocultarTests ? '🙈' : '👁'} Ocultar tests
              </span>
            </div>
          </div>

          {error && <div className="s-error">⚠ {error}</div>}

          {/* Tabla */}
          <div className="s-table">
            <div className="s-thead">
              <div>Titular</div>
              <div>Suscripciones</div>
              <div>Barrio · última actividad</div>
              <div className="right">Total facturado</div>
              <div />
            </div>

            {loading ? (
              <div className="s-empty">Cargando…</div>
            ) : filtrados.length === 0 ? (
              <div className="s-empty">No hay titulares para este filtro{ocultarTests && testsOcultos > 0 ? ` (${testsOcultos} test ocultos)` : ''}.</div>
            ) : filtrados.map(t => {
              const rel = haceCuanto(t.ultima_actividad)
              const activo5min = t.ultima_actividad && (Date.now() - new Date(t.ultima_actividad).getTime()) < 5 * 60000
              return (
                <div key={t.profile_id} className={`s-row ${t.estado_dominante === 'activa' ? 'row-activo' : ''}`}
                  onClick={() => nav(`/admin/clientes/${t.profile_id}`)}>
                  <div className="titular">
                    <div className={`avatar ${AVATAR_CLASS[t.estado_dominante] || 'av-gray'}`}>{iniciales(t.nombre)}</div>
                    <div>
                      <div className="titular-name">
                        {t.nombre || 'Sin nombre'}
                        {t.es_test && <span className="test-tag">TEST</span>}
                      </div>
                      <div className="titular-meta">{t.email}{t.phone ? ` · ${t.phone}` : ''}</div>
                    </div>
                  </div>
                  <div>
                    <div className="badges">
                      {badgesDe(t).map((b, i) => <span key={i} className={`badge ${b.cls}`}>{b.txt}</span>)}
                    </div>
                    {t.kit_nombre && <div className="row-meta">{t.kit_nombre}</div>}
                  </div>
                  <div>
                    <div className="barrio">{t.barrio || '—'}</div>
                    <div className="last-activity">
                      {activo5min && <span className="live-dot" />}
                      {rel ? (activo5min ? 'Activo ' : '') + rel : 'Sin actividad'}
                    </div>
                  </div>
                  <div>
                    <div className={`money ${Number(t.total_facturado) > 0 ? '' : 'money-muted'}`}>{fmtMoney(t.total_facturado)}</div>
                    <div className="money-sub">{t.cobros > 0 ? `${t.cobros} cobro${t.cobros > 1 ? 's' : ''}` : 'Sin cobros'}</div>
                  </div>
                  <div className="chev">›</div>
                </div>
              )
            })}
          </div>

          <div className="s-footer">
            <div>{filtrados.length} titular{filtrados.length === 1 ? '' : 'es'} · {totalSusc} suscripcion{totalSusc === 1 ? '' : 'es'}{testsOcultos > 0 ? ` · ${testsOcultos} test ocultos por filtro` : ''}</div>
            <div>Click en fila → Ficha 360</div>
          </div>

        </div>
      </div>
    </AdminLayout>
  )
}

// Estilos LIGHT SCOPEADOS a .suscv2 — consistentes con el admin Dashboard.
// Tokens: bg azul claro, cards blancas, texto navy, teal oficial #00A8B5.
const SUSCV2_CSS = `
.suscv2 {
  --nv: #1A3461; --bg: #F8FAFC; --teal: #00A8B5; --teal-hi: #008E99;
  --green: #16A34A; --green-bg: #DCFCE7;
  --sky: #0EA5E9; --sky-bg: #E0F2FE;
  --red: #DC2626; --red-bg: #FEE2E2;
  --amber: #CA8A04; --amber-bg: #FEF9C3;
  --ink1: #1A3461; --ink2: #475569; --ink3: #64748B; --ink4: #94A3B8;
  --line: #E2E8F0; --line-h: #E2E8F0; --surf-h: #F8FAFC;
  --card-sh: 0 1px 3px rgba(15,23,42,.05);
  color: var(--ink1); font-size: 14px;
}
.suscv2 .panel { background: transparent; max-width: 1240px; margin: 0 auto; }
.suscv2 .s-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 22px; gap: 12px; flex-wrap: wrap; }
.suscv2 .s-eyebrow { font-size: 11px; color: var(--ink3); text-transform: uppercase; letter-spacing: .06em; font-weight: 600; }
.suscv2 .s-title { font-size: 22px; font-weight: 900; margin-top: 4px; letter-spacing: -.02em; color: var(--nv); }
.suscv2 .s-header-actions { display: flex; gap: 8px; }
.suscv2 .s-btn { border: none; border-radius: 8px; padding: 9px 14px; font-size: 12px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; font-family: inherit; transition: all .15s; text-decoration: none; }
.suscv2 .s-btn-primary { background: var(--teal); color: #0F1F3D; }
.suscv2 .s-btn-primary:hover { background: var(--teal-hi); color: #fff; text-decoration: none; }
.suscv2 .s-btn-secondary { background: #fff; color: var(--nv); border: 1px solid var(--line-h); }
.suscv2 .s-btn-secondary:hover:not(:disabled) { background: var(--surf-h); }
.suscv2 .s-btn:disabled { opacity: .4; cursor: not-allowed; }

.suscv2 .kpi-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 12px; margin-bottom: 22px; }
.suscv2 .kpi { background: #fff; border: 1px solid var(--line); border-radius: 12px; padding: 14px 16px; box-shadow: var(--card-sh); }
.suscv2 .kpi-green { background: var(--green-bg); border-color: rgba(22,163,74,.25); }
.suscv2 .kpi-sky { background: var(--sky-bg); border-color: rgba(14,165,233,.25); }
.suscv2 .kpi-red { background: var(--red-bg); border-color: rgba(220,38,38,.22); }
.suscv2 .kpi-amber { background: var(--amber-bg); border-color: rgba(202,138,4,.28); }
.suscv2 .kpi-label { font-size: 10.5px; color: var(--ink3); text-transform: uppercase; letter-spacing: .05em; font-weight: 700; margin-bottom: 6px; }
.suscv2 .kpi-value { font-size: 24px; font-weight: 800; letter-spacing: -.02em; color: var(--nv); }
.suscv2 .kpi-green .kpi-value { color: var(--green); }
.suscv2 .kpi-sky .kpi-value { color: var(--sky); }
.suscv2 .kpi-red .kpi-value { color: var(--red); }
.suscv2 .kpi-amber .kpi-value { color: var(--amber); }
.suscv2 .kpi-sub { font-size: 10.5px; color: var(--ink3); margin-top: 4px; }

.suscv2 .toolbar { background: #fff; border: 1px solid var(--line); border-radius: 12px; padding: 12px; margin-bottom: 16px; display: flex; gap: 10px; align-items: center; flex-wrap: wrap; box-shadow: var(--card-sh); }
.suscv2 .search { flex: 1; min-width: 240px; background: var(--bg); border: 1px solid var(--line); border-radius: 8px; padding: 8px 12px; display: flex; align-items: center; gap: 8px; }
.suscv2 .search input { flex: 1; background: transparent; border: none; color: var(--ink1); font-family: inherit; font-size: 13px; outline: none; }
.suscv2 .search input::placeholder { color: var(--ink4); }
.suscv2 .s-ic { font-size: 13px; opacity: .6; }
.suscv2 .filters { display: flex; gap: 6px; flex-wrap: wrap; align-items: center; }
.suscv2 .pill { background: #fff; color: var(--ink2); border: 1px solid var(--line-h); border-radius: 8px; padding: 7px 12px; font-size: 11.5px; cursor: pointer; display: inline-flex; align-items: center; gap: 5px; font-weight: 500; transition: all .15s; }
.suscv2 .pill:hover { border-color: var(--teal); color: var(--nv); }
.suscv2 .pill-active { background: rgba(0,168,181,.12); color: var(--teal-hi); border-color: rgba(0,168,181,.40); font-weight: 600; }
.suscv2 .pill-danger { color: var(--ink2); }
.suscv2 .pill-danger-on { background: var(--red-bg); color: var(--red); border-color: rgba(220,38,38,.30); font-weight: 600; }
.suscv2 .divider-v { width: 1px; height: 22px; background: var(--line-h); margin: 0 4px; }

.suscv2 .s-table { border: 1px solid var(--line); border-radius: 12px; overflow: hidden; background: #fff; box-shadow: var(--card-sh); }
.suscv2 .s-thead, .suscv2 .s-row { display: grid; grid-template-columns: 2.2fr 1.6fr 1.2fr 1fr .4fr; gap: 12px; }
.suscv2 .s-thead { background: var(--bg); padding: 11px 18px; font-size: 11.5px; color: var(--ink3); text-transform: uppercase; letter-spacing: .05em; font-weight: 700; }
.suscv2 .s-thead .right { text-align: right; }
.suscv2 .s-row { padding: 14px 18px; align-items: center; border-top: 1px solid var(--line); cursor: pointer; transition: background .15s; }
.suscv2 .s-row:hover { background: var(--surf-h); }
.suscv2 .row-activo { background: rgba(22,163,74,.05); }
.suscv2 .row-activo:hover { background: rgba(22,163,74,.09); }
.suscv2 .s-empty { padding: 32px 18px; text-align: center; color: var(--ink3); font-size: 13px; }

.suscv2 .titular { display: flex; align-items: center; gap: 12px; }
.suscv2 .avatar { width: 34px; height: 34px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; flex-shrink: 0; }
.suscv2 .av-green { background: var(--green-bg); color: var(--green); }
.suscv2 .av-amber { background: var(--amber-bg); color: var(--amber); }
.suscv2 .av-red { background: var(--red-bg); color: var(--red); }
.suscv2 .av-gray { background: #F1F5F9; color: var(--ink2); }
.suscv2 .titular-name { font-size: 13.5px; font-weight: 600; color: var(--nv); display: flex; align-items: center; gap: 6px; }
.suscv2 .titular-meta { font-size: 11.5px; color: var(--ink3); margin-top: 2px; }
.suscv2 .test-tag { font-size: 9px; background: var(--amber-bg); color: var(--amber); padding: 2px 7px; border-radius: 999px; font-weight: 700; letter-spacing: .04em; }

.suscv2 .badges { display: flex; gap: 4px; flex-wrap: wrap; margin-bottom: 4px; }
.suscv2 .badge { font-size: 11px; padding: 3px 10px; border-radius: 999px; font-weight: 700; }
.suscv2 .bg-green { background: var(--green-bg); color: var(--green); }
.suscv2 .bg-amber { background: var(--amber-bg); color: var(--amber); }
.suscv2 .bg-red { background: var(--red-bg); color: var(--red); }
.suscv2 .bg-muted { background: #F1F5F9; color: var(--ink3); }
.suscv2 .row-meta { font-size: 10.5px; color: var(--ink3); }

.suscv2 .barrio { font-size: 12.5px; font-weight: 600; color: var(--nv); }
.suscv2 .last-activity { font-size: 10.5px; color: var(--ink3); margin-top: 2px; }
.suscv2 .live-dot { width: 6px; height: 6px; background: var(--green); border-radius: 50%; display: inline-block; margin-right: 4px; animation: suscv2pulse 2s ease-in-out infinite; }
@keyframes suscv2pulse { 0%,100% { opacity: 1 } 50% { opacity: .4 } }

.suscv2 .money { font-size: 13.5px; font-weight: 700; color: var(--teal-hi); text-align: right; }
.suscv2 .money-muted { color: var(--ink3); font-weight: 500; }
.suscv2 .money-sub { font-size: 10.5px; color: var(--ink3); text-align: right; margin-top: 2px; }
.suscv2 .chev { text-align: right; color: var(--ink4); font-size: 18px; }
.suscv2 .s-row:hover .chev { color: var(--teal); }

.suscv2 .s-footer { display: flex; align-items: center; justify-content: space-between; margin-top: 16px; padding-top: 12px; border-top: 1px solid var(--line); font-size: 11.5px; color: var(--ink3); }
.suscv2 .s-error { background: rgba(226,75,74,.08); border: 1px solid rgba(198,64,63,.30); color: var(--red); padding: 10px 14px; border-radius: 8px; margin-bottom: 14px; font-size: 12.5px; }

@media (max-width: 780px) {
  .suscv2 .s-thead, .suscv2 .s-row { grid-template-columns: 2fr 1fr .4fr; }
  .suscv2 .s-thead > *:nth-child(3), .suscv2 .s-thead > *:nth-child(4),
  .suscv2 .s-row > *:nth-child(3), .suscv2 .s-row > *:nth-child(4) { display: none; }
}
`
